import React, { useState, useEffect } from 'react';
import {
  PlusIcon,
  EyeIcon,
  ExclamationTriangleIcon,
  XMarkIcon,
  ClockIcon,
  ChatBubbleLeftRightIcon,
  UserIcon,
  CheckCircleIcon,
  XCircleIcon,
  PlayIcon,
  StopIcon,
  CalendarIcon,
} from '@heroicons/react/24/outline';
import { Session, SessionStatus, SessionCreateRequest, Language, SessionListResponse, CreateSessionResponse } from '../types';
import { sessionsApi, handleApiError, api } from '../utils/api';
import { LanguageSearch } from '../components/LanguageSearch';

export function Sessions() {
  const [sessions, setSessions] = useState<Session[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [showCreateModal, setShowCreateModal] = useState(false);
  const [selectedSession, setSelectedSession] = useState<Session | null>(null);
  const [showDetailsModal, setShowDetailsModal] = useState(false);
  const [showSuccessModal, setShowSuccessModal] = useState(false);
  const [createdSessionData, setCreatedSessionData] = useState<CreateSessionResponse | null>(null);
  const [newSession, setNewSession] = useState<SessionCreateRequest>({
    message_limit: 20,
    session_lifetime_hours: 24,
    client_username: undefined,
    agent_username: undefined,
    agent_matrix_username: undefined,
    client_language_id: undefined,
    agent_language_id: undefined
  });
  const [creating, setCreating] = useState(false);
  const [statusFilter, setStatusFilter] = useState<SessionStatus | 'all'>('all');
  const [searchQuery, setSearchQuery] = useState<string>('');
  const [searchInput, setSearchInput] = useState<string>('');
  // eslint-disable-next-line @typescript-eslint/no-unused-vars
  const [languages, setLanguages] = useState<Language[]>([]);
  
  // Pagination state
  const [currentPage, setCurrentPage] = useState(1);
  const [perPage, setPerPage] = useState(20);
  const [totalPages, setTotalPages] = useState(1);
  const [totalSessions, setTotalSessions] = useState(0);
  const [hasNext, setHasNext] = useState(false);
  const [hasPrev, setHasPrev] = useState(false);

  useEffect(() => {
    loadSessions();
    loadLanguages();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [statusFilter, currentPage, perPage, searchQuery]);

  // Debounce search input
  useEffect(() => {
    const timeoutId = setTimeout(() => {
      setSearchQuery(searchInput);
    }, 1500); // 500ms delay

    return () => clearTimeout(timeoutId);
  }, [searchInput]);

  const loadSessions = async () => {
    try {
      setLoading(true);
      setError(null);
      // When statusFilter is 'all', don't pass any status parameter
      const response = await sessionsApi.getAll(
        statusFilter === 'all' ? undefined : statusFilter,
        currentPage,
        perPage,
        searchQuery || undefined
      );
      const data: SessionListResponse = response.data;
      console.log('API Response:', data); // Debug log
      setSessions(Array.isArray(data.sessions) ? data.sessions : []);
      setTotalSessions(data.total || 0);
      setTotalPages(data.total_pages || 1);
      setHasNext(data.has_next || false);
      setHasPrev(data.has_prev || false);
    } catch (err) {
      setError(handleApiError(err));
      setSessions([]); // Ensure sessions is always an array
    } finally {
      setLoading(false);
    }
  };

  const loadLanguages = async () => {
    try {
      // Load initial languages (first 20)
      const response = await api.get('api/v1/languages/search?per_page=20');
      setLanguages(Array.isArray(response.data) ? response.data : []);
    } catch (err) {
      console.error('Failed to load languages:', err);
      setLanguages([]); // Ensure languages is always an array
    }
  };

  const handleCreateSession = async () => {
    try {
      setCreating(true);
      setError(null);
      const response = await sessionsApi.create(newSession);
      const sessionData = response.data;
      
      setCreatedSessionData(sessionData);
      await loadSessions();
      setShowCreateModal(false);
      setShowSuccessModal(true);
      setNewSession({
        message_limit: 20,
        session_lifetime_hours: 24,
        client_username: undefined,
        agent_username: undefined,
        agent_matrix_username: undefined,
        client_language_id: undefined,
        agent_language_id: undefined
      });
    } catch (err) {
      setError(handleApiError(err));
    } finally {
      setCreating(false);
    }
  };

  const handleViewDetails = (session: Session) => {
    setSelectedSession(session);
    setShowDetailsModal(true);
  };

  const handlePageChange = (page: number) => {
    setCurrentPage(page);
  };

  const handlePerPageChange = (newPerPage: number) => {
    setPerPage(newPerPage);
    setCurrentPage(1); // Reset to first page when changing per page
  };

  const handleSearchChange = (query: string) => {
    setSearchInput(query);
    setCurrentPage(1); // Reset to first page when searching
  };

  const handleClearSearch = () => {
    setSearchInput('');
    setSearchQuery('');
    setCurrentPage(1);
  };

  const formatDate = (dateString: string) => {
    return new Date(dateString).toLocaleString();
  };

  const getStatusColor = (status: SessionStatus) => {
    switch (status) {
      case SessionStatus.PRE_CREATED:
        return 'bg-indigo-100 text-indigo-800';
      case SessionStatus.ACTIVE:
        return 'bg-green-100 text-green-800';
      case SessionStatus.PENDING:
        return 'bg-yellow-100 text-yellow-800';
      case SessionStatus.COMPLETED:
        return 'bg-blue-100 text-blue-800';
      case SessionStatus.EXPIRED:
        return 'bg-red-100 text-red-800';
      case SessionStatus.TERMINATED:
        return 'bg-gray-100 text-gray-800';
      default:
        return 'bg-gray-100 text-gray-800';
    }
  };

  const getStatusIcon = (status: SessionStatus) => {
    switch (status) {
      case SessionStatus.PRE_CREATED:
        return <ClockIcon className="h-4 w-4" />;
      case SessionStatus.ACTIVE:
        return <PlayIcon className="h-4 w-4" />;
      case SessionStatus.PENDING:
        return <ClockIcon className="h-4 w-4" />;
      case SessionStatus.COMPLETED:
        return <CheckCircleIcon className="h-4 w-4" />;
      case SessionStatus.EXPIRED:
        return <XCircleIcon className="h-4 w-4" />;
      case SessionStatus.TERMINATED:
        return <StopIcon className="h-4 w-4" />;
      default:
        return <ExclamationTriangleIcon className="h-4 w-4" />;
    }
  };

  if (loading) {
    return (
      <div className="flex justify-center items-center h-64">
        <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-blue-600"></div>
      </div>
    );
  }

  return (
    <div className="space-y-6">
      <div className="md:flex md:items-center md:justify-between">
        <div className="flex-1 min-w-0">
          <h1 className="text-2xl font-bold leading-7 text-gray-900 sm:text-3xl sm:truncate">
            Sessions
          </h1>
          <p className="mt-1 text-sm text-gray-500">
            Manage client-agent communication sessions
          </p>
        </div>
        <div className="mt-4 flex md:mt-0 md:ml-4 space-x-3">
          {/* Search Input */}
          <div className="relative">
            <input
              type="text"
              placeholder="Search by session ID..."
              value={searchInput}
              onChange={(e) => handleSearchChange(e.target.value)}
              className="inline-flex items-center px-3 py-2 border border-gray-300 rounded-md shadow-sm text-sm font-medium text-gray-700 bg-white hover:bg-gray-50 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-blue-500 w-64"
            />
            {searchInput && (
              <button
                onClick={handleClearSearch}
                className="absolute right-2 top-1/2 transform -translate-y-1/2 text-gray-400 hover:text-gray-600"
              >
                <XMarkIcon className="h-4 w-4" />
              </button>
            )}
          </div>
          
          <select
            value={statusFilter}
            onChange={(e) => setStatusFilter(e.target.value as SessionStatus | 'all')}
            className="inline-flex items-center px-3 py-2 border border-gray-300 rounded-md shadow-sm text-sm font-medium text-gray-700 bg-white hover:bg-gray-50 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-blue-500"
          >
            <option value="all">All Status</option>
            <option value={SessionStatus.PRE_CREATED}>Pre-created</option>
            <option value={SessionStatus.PENDING}>Pending</option>
            <option value={SessionStatus.ACTIVE}>Active</option>
            <option value={SessionStatus.COMPLETED}>Completed</option>
            <option value={SessionStatus.EXPIRED}>Expired</option>
            <option value={SessionStatus.TERMINATED}>Terminated</option>
          </select>
          <button
            className="inline-flex items-center px-4 py-2 border border-transparent rounded-md shadow-sm text-sm font-medium text-white bg-blue-600 hover:bg-blue-700 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-blue-500"
            onClick={() => setShowCreateModal(true)}
          >
            <PlusIcon className="h-4 w-4 mr-2" />
            Create Session
          </button>
        </div>
      </div>

      {error && (
        <div className="bg-red-50 border border-red-200 rounded-md p-4">
          <div className="flex">
            <XCircleIcon className="h-5 w-5 text-red-400" />
            <div className="ml-3">
              <h3 className="text-sm font-medium text-red-800">Error</h3>
              <div className="mt-2 text-sm text-red-700">{error}</div>
            </div>
          </div>
        </div>
      )}

      <div className="bg-white shadow overflow-hidden sm:rounded-md">
        <ul className="divide-y divide-gray-200">
          {!Array.isArray(sessions) || sessions.length === 0 ? (
            <li className="px-6 py-12 text-center">
              <ChatBubbleLeftRightIcon className="mx-auto h-12 w-12 text-gray-400" />
              <h3 className="mt-2 text-sm font-medium text-gray-900">No sessions</h3>
              <p className="mt-1 text-sm text-gray-500">
                Get started by creating a new session.
              </p>
              <div className="mt-6">
                <button
                  type="button"
                  className="inline-flex items-center px-4 py-2 border border-transparent shadow-sm text-sm font-medium rounded-md text-white bg-blue-600 hover:bg-blue-700 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-blue-500"
                  onClick={() => setShowCreateModal(true)}
                >
                  <PlusIcon className="-ml-1 mr-2 h-5 w-5" />
                  Create Session
                </button>
              </div>
            </li>
          ) : (
            Array.isArray(sessions) ? sessions.map((session) => (
              <li key={session.id} className="px-6 py-4">
                <div className="flex items-center justify-between">
                  <div className="flex items-center">
                    <div className="flex-shrink-0">
                      <div className="h-10 w-10 rounded-full bg-blue-100 flex items-center justify-center">
                        <ChatBubbleLeftRightIcon className="h-6 w-6 text-blue-600" />
                      </div>
                    </div>
                    <div className="ml-4">
                      <div className="flex items-center">
                        <p className="text-sm font-medium text-gray-900">
                          Session {session.session_id}
                        </p>
                        <span className={`ml-2 inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium ${getStatusColor(session.status)}`}>
                          {getStatusIcon(session.status)}
                          <span className="ml-1">{session.status}</span>
                        </span>
                      </div>
                    </div>
                  </div>
                  <div className="flex items-center space-x-4">
                    <div className="text-right">
                      <div className="text-sm text-gray-900">
                        {session.messages_sent_by_client} / {session.message_limit} client messages
                      </div>
                      <div className="text-xs text-gray-500">
                        Total: {session.messages_sent_by_client + session.messages_sent_by_agent} messages
                      </div>
                      <div className="text-xs text-gray-500">
                        Expires: {formatDate(session.expires_at)}
                      </div>
                    </div>

                    <button
                      onClick={() => handleViewDetails(session)}
                      className="text-blue-600 hover:text-blue-800"
                    >
                      <EyeIcon className="h-5 w-5" />
                    </button>
                  </div>
                </div>
                <div className="mt-4 grid grid-cols-2 md:grid-cols-6 gap-4">
                  <div className="bg-gray-50 rounded-lg p-3">
                    <div className="flex items-center">
                      <UserIcon className="h-5 w-5 text-blue-500" />
                      <span className="ml-2 text-sm font-medium text-gray-900">
                        {session.messages_sent_by_client}
                      </span>
                    </div>
                    <p className="text-xs text-gray-500">Client Messages (Limited)</p>
                  </div>

                  <div className="bg-gray-50 rounded-lg p-3">
                    <div className="flex items-center">
                      <UserIcon className="h-5 w-5 text-green-500" />
                      <span className="ml-2 text-sm font-medium text-gray-900">
                        {session.messages_sent_by_agent}
                      </span>
                    </div>
                    <p className="text-xs text-gray-500">Agent Messages (Unlimited)</p>
                  </div>

                  <div className="bg-gray-50 rounded-lg p-3">
                    <div className="flex items-center">
                      <UserIcon className="h-5 w-5 text-indigo-500" />
                      <span className="ml-2 text-sm font-medium text-gray-900">
                        {session.client_username || 'Not set'}
                      </span>
                    </div>
                    <p className="text-xs text-gray-500">Client Username</p>
                  </div>

                  <div className="bg-gray-50 rounded-lg p-3">
                    <div className="flex items-center">
                      <UserIcon className="h-5 w-5 text-teal-500" />
                      <span className="ml-2 text-sm font-medium text-gray-900">
                        {session.agent_username || 'Not set'}
                      </span>
                    </div>
                    <p className="text-xs text-gray-500">Agent Username</p>
                  </div>

                  <div className="bg-gray-50 rounded-lg p-3">
                    <div className="flex items-center">
                      <CalendarIcon className="h-5 w-5 text-yellow-500" />
                      <span className="ml-2 text-sm font-medium text-gray-900">
                        {session.session_lifetime_hours}h
                      </span>
                    </div>
                    <p className="text-xs text-gray-500">Lifetime</p>
                  </div>

                  <div className="bg-gray-50 rounded-lg p-3">
                    <div className="flex items-center">
                      <ClockIcon className="h-5 w-5 text-purple-500" />
                      <span className="ml-2 text-sm font-medium text-gray-900">
                        {formatDate(session.created_at)}
                      </span>
                    </div>
                    <p className="text-xs text-gray-500">Created</p>
                  </div>
                </div>
              </li>
            )) : null
          )}
        </ul>
      </div>

      {/* Pagination */}
      {Array.isArray(sessions) && sessions.length > 0 && (
        <div className="bg-white px-4 py-3 flex items-center justify-between border-t border-gray-200 sm:px-6">
          <div className="flex-1 flex justify-between sm:hidden">
            <button
              onClick={() => handlePageChange(currentPage - 1)}
              disabled={!hasPrev}
              className="relative inline-flex items-center px-4 py-2 border border-gray-300 text-sm font-medium rounded-md text-gray-700 bg-white hover:bg-gray-50 disabled:opacity-50 disabled:cursor-not-allowed"
            >
              Previous
            </button>
            <button
              onClick={() => handlePageChange(currentPage + 1)}
              disabled={!hasNext}
              className="ml-3 relative inline-flex items-center px-4 py-2 border border-gray-300 text-sm font-medium rounded-md text-gray-700 bg-white hover:bg-gray-50 disabled:opacity-50 disabled:cursor-not-allowed"
            >
              Next
            </button>
          </div>
          <div className="hidden sm:flex-1 sm:flex sm:items-center sm:justify-between">
            <div>
              <p className="text-sm text-gray-700">
                Showing{' '}
                <span className="font-medium">{(currentPage - 1) * perPage + 1}</span>
                {' '}to{' '}
                <span className="font-medium">
                  {Math.min(currentPage * perPage, totalSessions)}
                </span>
                {' '}of{' '}
                <span className="font-medium">{totalSessions}</span>
                {' '}results
              </p>
            </div>
            <div className="flex items-center space-x-4">
              <div className="flex items-center space-x-2">
                <label htmlFor="per-page" className="text-sm text-gray-700">
                  Per page:
                </label>
                <select
                  id="per-page"
                  value={perPage}
                  onChange={(e) => handlePerPageChange(parseInt(e.target.value))}
                  className="border-gray-300 rounded-md shadow-sm focus:ring-blue-500 focus:border-blue-500 sm:text-sm"
                >
                  <option value={10}>10</option>
                  <option value={20}>20</option>
                  <option value={50}>50</option>
                  <option value={100}>100</option>
                </select>
              </div>
              <nav className="relative z-0 inline-flex rounded-md shadow-sm -space-x-px" aria-label="Pagination">
                <button
                  onClick={() => handlePageChange(currentPage - 1)}
                  disabled={!hasPrev}
                  className="relative inline-flex items-center px-2 py-2 rounded-l-md border border-gray-300 bg-white text-sm font-medium text-gray-500 hover:bg-gray-50 disabled:opacity-50 disabled:cursor-not-allowed"
                >
                  <span className="sr-only">Previous</span>
                  <svg className="h-5 w-5" xmlns="http://www.w3.org/2000/svg" viewBox="0 0 20 20" fill="currentColor" aria-hidden="true">
                    <path fillRule="evenodd" d="M12.707 5.293a1 1 0 010 1.414L9.414 10l3.293 3.293a1 1 0 01-1.414 1.414l-4-4a1 1 0 010-1.414l4-4a1 1 0 011.414 0z" clipRule="evenodd" />
                  </svg>
                </button>
                
                {/* Page numbers */}
                {Array.from({ length: Math.min(5, totalPages) }, (_, i) => {
                  let pageNum: number;
                  if (totalPages <= 5) {
                    pageNum = i + 1;
                  } else if (currentPage <= 3) {
                    pageNum = i + 1;
                  } else if (currentPage >= totalPages - 2) {
                    pageNum = totalPages - 4 + i;
                  } else {
                    pageNum = currentPage - 2 + i;
                  }
                  
                  return (
                    <button
                      key={pageNum}
                      onClick={() => handlePageChange(pageNum)}
                      className={`relative inline-flex items-center px-4 py-2 border text-sm font-medium ${
                        pageNum === currentPage
                          ? 'z-10 bg-blue-50 border-blue-500 text-blue-600'
                          : 'bg-white border-gray-300 text-gray-500 hover:bg-gray-50'
                      }`}
                    >
                      {pageNum}
                    </button>
                  );
                })}
                
                <button
                  onClick={() => handlePageChange(currentPage + 1)}
                  disabled={!hasNext}
                  className="relative inline-flex items-center px-2 py-2 rounded-r-md border border-gray-300 bg-white text-sm font-medium text-gray-500 hover:bg-gray-50 disabled:opacity-50 disabled:cursor-not-allowed"
                >
                  <span className="sr-only">Next</span>
                  <svg className="h-5 w-5" xmlns="http://www.w3.org/2000/svg" viewBox="0 0 20 20" fill="currentColor" aria-hidden="true">
                    <path fillRule="evenodd" d="M7.293 14.707a1 1 0 010-1.414L10.586 10 7.293 6.707a1 1 0 011.414-1.414l4 4a1 1 0 010 1.414l-4 4a1 1 0 01-1.414 0z" clipRule="evenodd" />
                  </svg>
                </button>
              </nav>
            </div>
          </div>
        </div>
      )}

      {/* Create Session Modal */}
      {showCreateModal && (
        <div className="fixed inset-0 bg-gray-600 bg-opacity-50 overflow-y-auto h-full w-full z-50">
          <div className="relative top-10 mx-auto p-5 border w-full max-w-md shadow-lg rounded-md bg-white max-h-[90vh] overflow-y-auto">
            <div className="mt-3">
              <h3 className="text-lg font-medium text-gray-900 mb-4">Create New Session</h3>

              <div className="space-y-4">
                <div>
                  <label className="block text-sm font-medium text-gray-700">
                    Client Username (Optional)
                  </label>
                  <input
                    type="text"
                    value={newSession.client_username || ''}
                    onChange={(e) => setNewSession({
                      ...newSession,
                      client_username: e.target.value || undefined
                    })}
                    className="mt-1 block w-full border-gray-300 rounded-md shadow-sm focus:ring-blue-500 focus:border-blue-500 sm:text-sm"
                    placeholder="e.g., username"
                  />
                </div>

                <div>
                  <label className="block text-sm font-medium text-gray-700">
                    Agent Username (Optional)
                  </label>
                  <input
                    type="text"
                    value={newSession.agent_username || ''}
                    onChange={(e) => setNewSession({
                      ...newSession,
                      agent_username: e.target.value || undefined
                    })}
                    className="mt-1 block w-full border-gray-300 rounded-md shadow-sm focus:ring-blue-500 focus:border-blue-500 sm:text-sm"
                    placeholder="e.g., agent_username"
                  />
                </div>

                <div>
                  <label className="block text-sm font-medium text-gray-700">
                    Agent Matrix Username (Optional)
                  </label>
                  <input
                    type="text"
                    value={newSession.agent_matrix_username || ''}
                    onChange={(e) => setNewSession({
                      ...newSession,
                      agent_matrix_username: e.target.value || undefined
                    })}
                    className="mt-1 block w-full border-gray-300 rounded-md shadow-sm focus:ring-blue-500 focus:border-blue-500 sm:text-sm"
                    placeholder="e.g., @user:matrix.org"
                  />
                  <p className="mt-1 text-xs text-gray-500">
                    Format: @username:server.com
                  </p>
                </div>

                <div>
                  <label className="block text-sm font-medium text-gray-700">
                    Client Language (Optional)
                  </label>
                  <LanguageSearch
                    value={newSession.client_language_id}
                    onChange={(languageId) => setNewSession({
                      ...newSession,
                      client_language_id: languageId
                    })}
                    placeholder="Search for a language..."
                    className="mt-1"
                  />
                </div>

                <div>
                  <label className="block text-sm font-medium text-gray-700">
                    Agent Language (Optional)
                  </label>
                  <LanguageSearch
                    value={newSession.agent_language_id}
                    onChange={(languageId) => setNewSession({
                      ...newSession,
                      agent_language_id: languageId
                    })}
                    placeholder="Search for a language..."
                    className="mt-1"
                  />
                </div>

                <div>
                  <label className="block text-sm font-medium text-gray-700">
                    Message Limit
                  </label>
                  <input
                    type="number"
                    value={newSession.message_limit || 20}
                    onChange={(e) => setNewSession({
                      ...newSession,
                      message_limit: parseInt(e.target.value) || 20
                    })}
                    className="mt-1 block w-full border-gray-300 rounded-md shadow-sm focus:ring-blue-500 focus:border-blue-500 sm:text-sm"
                    min="1"
                    max="1000"
                  />
                </div>

                <div>
                  <label className="block text-sm font-medium text-gray-700">
                    Session Lifetime (hours)
                  </label>
                  <input
                    type="number"
                    value={newSession.session_lifetime_hours || 24}
                    onChange={(e) => setNewSession({
                      ...newSession,
                      session_lifetime_hours: parseInt(e.target.value) || 24
                    })}
                    className="mt-1 block w-full border-gray-300 rounded-md shadow-sm focus:ring-blue-500 focus:border-blue-500 sm:text-sm"
                    min="1"
                    max="168"
                  />
                </div>
              </div>

              <div className="flex justify-end space-x-3 mt-6">
                <button
                  onClick={() => setShowCreateModal(false)}
                  className="px-4 py-2 text-sm font-medium text-gray-700 bg-gray-100 border border-gray-300 rounded-md hover:bg-gray-200 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-gray-500"
                >
                  Cancel
                </button>
                <button
                  onClick={handleCreateSession}
                  disabled={creating}
                  className="px-4 py-2 text-sm font-medium text-white bg-blue-600 border border-transparent rounded-md hover:bg-blue-700 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-blue-500 disabled:opacity-50"
                >
                  {creating ? 'Creating...' : 'Create Session'}
                </button>
              </div>
            </div>
          </div>
        </div>
      )}

      {/* Session Details Modal */}
      {showDetailsModal && selectedSession && (
        <div className="fixed inset-0 bg-gray-600 bg-opacity-50 overflow-y-auto h-full w-full z-50">
          <div className="relative top-10 mx-auto p-6 border w-full max-w-lg shadow-xl rounded-lg bg-white max-h-[90vh] overflow-y-auto">
            <div className="mt-3">
              <h3 className="text-lg font-medium text-gray-900 mb-4">Session Details</h3>

              <div className="space-y-4">
                <div>
                  <label className="block text-sm font-medium text-gray-700">Session ID</label>
                  <p className="mt-1 text-sm text-gray-900 font-mono">{selectedSession.session_id}</p>
                </div>

                <div>
                  <label className="block text-sm font-medium text-gray-700">Status</label>
                  <span className={`mt-1 inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium ${getStatusColor(selectedSession.status)}`}>
                    {getStatusIcon(selectedSession.status)}
                    <span className="ml-1">{selectedSession.status}</span>
                  </span>
                </div>

                <div>
                  <label className="block text-sm font-medium text-gray-700">Client</label>
                  <p className="mt-1 text-sm text-gray-900">
                    {selectedSession.client_username || selectedSession.client_user_id}
                  </p>
                </div>

                <div>
                  <label className="block text-sm font-medium text-gray-700">Agent</label>
                  <p className="mt-1 text-sm text-gray-900">
                    {selectedSession.agent_username || selectedSession.agent_group_id}
                  </p>
                </div>

                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-3">Messages Statistics</label>
                  <div className="grid grid-cols-3 gap-3">
                    <div className="bg-blue-50 rounded-lg p-3 border border-blue-200">
                      <div className="flex items-center justify-between mb-1">
                        <span className="text-xs font-medium text-blue-700">Client</span>
                        <ChatBubbleLeftRightIcon className="h-4 w-4 text-blue-600" />
                      </div>
                      <p className="text-2xl font-bold text-blue-900">{selectedSession.messages_sent_by_client}</p>
                      <p className="text-xs text-blue-600 mt-1">messages sent</p>
                    </div>
                    
                    <div className="bg-green-50 rounded-lg p-3 border border-green-200">
                      <div className="flex items-center justify-between mb-1">
                        <span className="text-xs font-medium text-green-700">Agent</span>
                        <UserIcon className="h-4 w-4 text-green-600" />
                      </div>
                      <p className="text-2xl font-bold text-green-900">{selectedSession.messages_sent_by_agent}</p>
                      <p className="text-xs text-green-600 mt-1">messages sent</p>
                    </div>
                    
                    <div className="bg-purple-50 rounded-lg p-3 border border-purple-200">
                      <div className="flex items-center justify-between mb-1">
                        <span className="text-xs font-medium text-purple-700">Limit</span>
                        <svg className="h-4 w-4 text-purple-600" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 12l2 2 4-4m6 2a9 9 0 11-18 0 9 9 0 0118 0z" />
                        </svg>
                      </div>
                      <p className="text-2xl font-bold text-purple-900">{selectedSession.message_limit}</p>
                      <p className="text-xs text-purple-600 mt-1">total allowed</p>
                    </div>
                  </div>
                  
                  <div className="mt-3 bg-gray-100 rounded-full h-2 overflow-hidden">
                    <div 
                      className="bg-blue-600 h-full transition-all duration-300"
                      style={{ 
                        width: `${Math.min(100, (selectedSession.messages_sent_by_client / selectedSession.message_limit) * 100)}%` 
                      }}
                    />
                  </div>
                  <p className="text-xs text-gray-500 mt-1 text-center">
                    {selectedSession.messages_sent_by_client} / {selectedSession.message_limit} messages used
                  </p>
                </div>

                <div>
                  <label className="block text-sm font-medium text-gray-700">Created</label>
                  <p className="mt-1 text-sm text-gray-900">{formatDate(selectedSession.created_at)}</p>
                </div>

                <div>
                  <label className="block text-sm font-medium text-gray-700">Expires</label>
                  <p className="mt-1 text-sm text-gray-900">{formatDate(selectedSession.expires_at)}</p>
                </div>

                {selectedSession.client_invite_link && (
                  <div className="bg-blue-50 rounded-lg p-3 border border-blue-200">
                    <label className="block text-sm font-medium text-gray-700 mb-2 flex items-center">
                      <ChatBubbleLeftRightIcon className="h-4 w-4 mr-2 text-blue-600" />
                      Client Invite Link
                    </label>
                    <div className="flex items-center justify-between gap-2">
                      <a
                        href={selectedSession.client_invite_link}
                        target="_blank"
                        rel="noopener noreferrer"
                        className="text-sm text-blue-600 hover:text-blue-800 break-all flex-1"
                      >
                        {selectedSession.client_invite_link}
                      </a>
                      <button
                        onClick={() => {
                          navigator.clipboard.writeText(selectedSession.client_invite_link!);
                        }}
                        className="px-2 py-1 text-xs text-blue-600 hover:text-blue-800 border border-blue-300 rounded hover:bg-blue-100 whitespace-nowrap"
                        title="Copy to clipboard"
                      >
                        Copy
                      </button>
                    </div>
                  </div>
                )}

                {selectedSession.agent_invite_link && (
                  <div>
                    <label className="block text-sm font-medium text-gray-700">Agent Invite Link</label>
                    <a
                      href={selectedSession.agent_invite_link}
                      target="_blank"
                      rel="noopener noreferrer"
                      className="mt-1 text-sm text-blue-600 hover:text-blue-800 break-all"
                    >
                      {selectedSession.agent_invite_link}
                    </a>
                  </div>
                )}

                {selectedSession.matrix_room_link && (
                  <div className="bg-purple-50 rounded-lg p-3 border border-purple-200">
                    <label className="block text-sm font-medium text-gray-700 mb-2 flex items-center">
                      <svg className="h-4 w-4 mr-2 text-purple-600" fill="currentColor" viewBox="0 0 20 20">
                        <path d="M2 5a2 2 0 012-2h7a2 2 0 012 2v4a2 2 0 01-2 2H9l-3 3v-3H4a2 2 0 01-2-2V5z" />
                        <path d="M15 7v2a4 4 0 01-4 4H9.828l-1.766 1.767c.28.149.599.233.938.233h2l3 3v-3h2a2 2 0 002-2V9a2 2 0 00-2-2h-1z" />
                      </svg>
                      Matrix Room Link
                    </label>
                    <div className="flex items-center justify-between gap-2">
                      <a
                        href={selectedSession.matrix_room_link}
                        target="_blank"
                        rel="noopener noreferrer"
                        className="text-sm text-purple-600 hover:text-purple-800 break-all flex-1"
                      >
                        {selectedSession.matrix_room_link}
                      </a>
                      <button
                        onClick={() => {
                          navigator.clipboard.writeText(selectedSession.matrix_room_link!);
                        }}
                        className="px-2 py-1 text-xs text-purple-600 hover:text-purple-800 border border-purple-300 rounded hover:bg-purple-100 whitespace-nowrap"
                        title="Copy to clipboard"
                      >
                        Copy
                      </button>
                    </div>
                  </div>
                )}

                {selectedSession.agent_matrix_username && (
                  <div>
                    <label className="block text-sm font-medium text-gray-700">Agent Matrix Username</label>
                    <p className="mt-1 text-sm text-gray-900 font-mono">{selectedSession.agent_matrix_username}</p>
                  </div>
                )}
              </div>

              <div className="flex justify-end mt-6">
                <button
                  onClick={() => setShowDetailsModal(false)}
                  className="px-4 py-2 text-sm font-medium text-gray-700 bg-gray-100 border border-gray-300 rounded-md hover:bg-gray-200 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-gray-500"
                >
                  Close
                </button>
              </div>
            </div>
          </div>
        </div>
      )}

      {/* Success Modal - Show after session creation */}
      {showSuccessModal && createdSessionData && (
        <div className="fixed inset-0 bg-gray-600 bg-opacity-50 overflow-y-auto h-full w-full z-50">
          <div className="relative top-10 mx-auto p-6 border w-full max-w-lg shadow-xl rounded-lg bg-white">
            <div className="flex items-center justify-between mb-4">
              <div className="flex items-center">
                <CheckCircleIcon className="h-8 w-8 text-green-500 mr-3" />
                <h3 className="text-xl font-semibold text-gray-900">Session Created Successfully!</h3>
              </div>
              <button
                onClick={() => {
                  setShowSuccessModal(false);
                  setCreatedSessionData(null);
                }}
                className="text-gray-400 hover:text-gray-600"
              >
                <XMarkIcon className="h-6 w-6" />
              </button>
            </div>

            <div className="space-y-4 mt-6">
              <div className="bg-gray-50 rounded-lg p-4">
                <label className="block text-sm font-medium text-gray-700 mb-2">
                  Session ID
                </label>
                <div className="flex items-center justify-between">
                  <code className="text-sm text-gray-900 font-mono bg-white px-3 py-2 rounded border flex-1 mr-2">
                    {createdSessionData.session_id}
                  </code>
                  <button
                    onClick={() => {
                      navigator.clipboard.writeText(createdSessionData.session_id);
                    }}
                    className="px-3 py-2 text-sm text-blue-600 hover:text-blue-800 border border-blue-300 rounded hover:bg-blue-50"
                  >
                    Copy
                  </button>
                </div>
              </div>

              <div className="bg-blue-50 rounded-lg p-4 border border-blue-200">
                <label className="block text-sm font-medium text-gray-700 mb-2 flex items-center">
                  <ChatBubbleLeftRightIcon className="h-4 w-4 mr-2 text-blue-600" />
                  Client Invite Link
                </label>
                <div className="flex items-center justify-between">
                  <a
                    href={createdSessionData.client_invite_link}
                    target="_blank"
                    rel="noopener noreferrer"
                    className="text-sm text-blue-600 hover:text-blue-800 break-all flex-1 mr-2"
                  >
                    {createdSessionData.client_invite_link}
                  </a>
                  <button
                    onClick={() => {
                      navigator.clipboard.writeText(createdSessionData.client_invite_link);
                    }}
                    className="px-3 py-2 text-sm text-blue-600 hover:text-blue-800 border border-blue-300 rounded hover:bg-blue-100 whitespace-nowrap"
                  >
                    Copy
                  </button>
                </div>
              </div>

              {createdSessionData.matrix_room_link && (
                <div className="bg-purple-50 rounded-lg p-4 border border-purple-200">
                  <label className="block text-sm font-medium text-gray-700 mb-2 flex items-center">
                    <svg className="h-4 w-4 mr-2 text-purple-600" fill="currentColor" viewBox="0 0 20 20">
                      <path d="M2 5a2 2 0 012-2h7a2 2 0 012 2v4a2 2 0 01-2 2H9l-3 3v-3H4a2 2 0 01-2-2V5z" />
                      <path d="M15 7v2a4 4 0 01-4 4H9.828l-1.766 1.767c.28.149.599.233.938.233h2l3 3v-3h2a2 2 0 002-2V9a2 2 0 00-2-2h-1z" />
                    </svg>
                    Matrix Room Link
                  </label>
                  <div className="flex items-center justify-between">
                    <a
                      href={createdSessionData.matrix_room_link}
                      target="_blank"
                      rel="noopener noreferrer"
                      className="text-sm text-purple-600 hover:text-purple-800 break-all flex-1 mr-2"
                    >
                      {createdSessionData.matrix_room_link}
                    </a>
                    <button
                      onClick={() => {
                        navigator.clipboard.writeText(createdSessionData.matrix_room_link!);
                      }}
                      className="px-3 py-2 text-sm text-purple-600 hover:text-purple-800 border border-purple-300 rounded hover:bg-purple-100 whitespace-nowrap"
                    >
                      Copy
                    </button>
                  </div>
                </div>
              )}

              <div className="bg-gray-50 rounded-lg p-4">
                <div className="grid grid-cols-2 gap-4">
                  <div>
                    <label className="block text-xs font-medium text-gray-500 mb-1">Message Limit</label>
                    <p className="text-lg font-semibold text-gray-900">{createdSessionData.message_limit}</p>
                  </div>
                  <div>
                    <label className="block text-xs font-medium text-gray-500 mb-1">Status</label>
                    <span className="inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium bg-green-100 text-green-800">
                      <CheckCircleIcon className="h-3 w-3 mr-1" />
                      Active
                    </span>
                  </div>
                </div>
              </div>
            </div>

            <div className="flex justify-end mt-6 space-x-3">
              <button
                onClick={() => {
                  setShowSuccessModal(false);
                  setCreatedSessionData(null);
                }}
                className="px-4 py-2 text-sm font-medium text-gray-700 bg-gray-100 border border-gray-300 rounded-md hover:bg-gray-200 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-gray-500"
              >
                Close
              </button>
              <button
                onClick={() => {
                  const foundSession = sessions.find(s => s.session_id === createdSessionData.session_id);
                  setShowSuccessModal(false);
                  setCreatedSessionData(null);
                  if (foundSession) {
                    handleViewDetails(foundSession);
                  }
                }}
                className="px-4 py-2 text-sm font-medium text-white bg-blue-600 border border-transparent rounded-md hover:bg-blue-700 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-blue-500"
              >
                View Details
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}
